import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, Tooltip, InputLabel, Table, Grid, Box, TablePagination, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Button, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Swal from 'sweetalert2';
import { gridSpacing } from 'config.js';

const apiUrl = 'https://api.academy.eurekagroup.id/course-details';
const coursesUrl = 'https://api.academy.eurekagroup.id/courses';
const addCourseDetailUrl = `${apiUrl}/add`;
const editCourseDetailUrl = `${apiUrl}/edit`;
const deleteCourseDetailUrl = (id) => `${apiUrl}/delete/${id}`;

const CourseDetailPage = () => {
  const [courseDetails, setCourseDetails] = useState([]);
  const [openEditSessionDialog, setOpenEditSessionDialog] = useState(false);
  const [courses, setCourses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCourseDetail, setSelectedCourseDetail] = useState(null);
  const [course, setCourse] = useState('');
  const [numberSession, setNumberSession] = useState(0);
  const [titleSession, setTitleSession] = useState('');
  const [sessionCategory, setSessionCategory] = useState('video');
  const [timeLimit, setTimeLimit] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [textContent, setTextContent] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [errors, setErrors] = useState({
    titleSession: false,
    course: false
  });

  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        const response = await axios.get(apiUrl);
        setCourseDetails(response.data);
      } catch (error) {
        console.error('Error fetching course details:', error);
      }
    };

    const fetchCourses = async () => {
      try {
        const response = await axios.get(coursesUrl);
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    fetchCourseDetails();
    fetchCourses();
  }, []);

  const handleSearchChange = (event) => setSearchText(event.target.value);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (courseDetail) => {
    if (courseDetail) {
      setSelectedCourseDetail(courseDetail);
      setCourse(courseDetail.id_course._id);
      setNumberSession(courseDetail.number_session);
      setTitleSession(courseDetail.title_session);
      setSessionCategory(courseDetail.session_category);
      setTimeLimit(courseDetail.time_limit);
      setVideoUrl(courseDetail.video_url || '');
      setTextContent(courseDetail.text_content || '');
    } else {
      setSelectedCourseDetail(null);
      setCourse('');
      setNumberSession(0);
      setTitleSession('');
      setSessionCategory('video');
      setTimeLimit(0);
      setVideoUrl('');
      setTextContent('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveCourseDetail = async () => {
    const courseDetailData = {
      id_course: course,
      number_session: numberSession,
      title_session: titleSession,
      sessions: [
        {
          title_isi_session: titleSession,
          ...(selectedCourseDetail ? {} : { time_limit: timeLimit }),
          video_url: sessionCategory === 'video' ? videoUrl : undefined,
          text_content: sessionCategory === 'text' ? textContent : undefined,
          ...(selectedCourseDetail ? {} : { session_category: sessionCategory }),
        }
      ]
    };
  
    if (selectedCourseDetail) {
      courseDetailData._id = selectedCourseDetail._id;
    }
  
    let formIsValid = true;
    const newErrors = { ...errors };
  
    if (!titleSession) {
      newErrors.titleSession = true;
      formIsValid = false;
    } else {
      newErrors.titleSession = false;
    }
  
    if (!course) {
      newErrors.course = true;
      formIsValid = false;
    } else {
      newErrors.course = false;
    }
  
    setErrors(newErrors);
  
    if (!formIsValid) {
      return;
    }
  
    const token = localStorage.getItem('token');
  
    try {
      if (selectedCourseDetail) {
        await axios.put(editCourseDetailUrl, courseDetailData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setMessage('Course Detail updated successfully');
      } else {
        const response = await axios.post(addCourseDetailUrl, courseDetailData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        console.log("New course detail added:", response.data);
        setCourseDetails(prevCourseDetails => [...prevCourseDetails, response.data]);
        console.log("Updated course details:", [...courseDetails, response.data]);
        setMessage('Course Detail added successfully');
      }
  
      await getCourseDetails();
      setSuccessPopup(true);
      handleCloseDialog();
      window.location.reload(); // Auto reload setelah save berhasil
  
    } catch (error) {
      console.error('Error saving course detail:', error);
      setMessage('Error saving course detail');
    }
  };
  
  const handleEditSession = async (sessionId) => {
    const sessionToEdit = courseDetails
      .flatMap(courseDetail => courseDetail.sessions)
      .find(session => session._id === sessionId);
  
    if (sessionToEdit) {
      const courseDetail = courseDetails.find(course => course.sessions.some(session => session._id === sessionId));
      setSelectedCourseDetail(courseDetail);
      setSelectedSession(sessionToEdit);
      setOpenEditSessionDialog(true);
    }
  };
  
  const handleSaveSessionDetails = async () => {
    if (!selectedCourseDetail || !selectedSession) {
      Swal.fire('Error', 'Selected course or session is not valid', 'error');
      return;
    }
  
    const updatedSessions = courseDetails.map(courseDetail => {
      if (courseDetail._id === selectedCourseDetail._id) {
        return {
          ...courseDetail,
          sessions: courseDetail.sessions.map(session =>
            session._id === selectedSession._id
              ? { ...session, ...selectedSession }
              : session
          )
        };
      }
      return courseDetail;
    });
  
    const token = localStorage.getItem('token');
  
    try {
      await axios.put('https://api.academy.eurekagroup.id/course-details/edit-sessions', {
        _id: selectedCourseDetail._id,
        sessions: updatedSessions.find(detail => detail._id === selectedCourseDetail._id).sessions
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
  
      setCourseDetails(updatedSessions);
      Swal.fire('Success', 'Session updated successfully', 'success');
      setOpenEditSessionDialog(false);
      window.location.reload(); // Auto reload setelah save berhasil
  
    } catch (error) {
      console.error('Error updating session details:', error);
      Swal.fire('Error', 'Failed to update session', 'error');
    }
  };
  
  // Function to fetch course details from the server
  const getCourseDetails = async () => {
    const token = localStorage.getItem('token'); // Retrieve token
  
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      console.log("Fetched course details:", response.data);  // Debug: Log the fetched data
  
      // Update course details with the latest data from the server
      setCourseDetails(response.data);  // Update state with the fetched data
  
    } catch (error) {
      console.error('Error fetching course details:', error);
    }
  };  

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      // Get the Bearer token (from localStorage, sessionStorage, or context)
      const token = localStorage.getItem('token'); // Replace with actual token retrieval
  
      try {
        await axios.delete(deleteCourseDetailUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Add Bearer token to headers
          }
        });
        setCourseDetails(courseDetails.filter(courseDetail => courseDetail._id !== id));
        setMessage('Course Detail deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting course detail:', error);
        setMessage('Error deleting course detail');
      }
    }
  };  

  const filteredCourseDetails = courseDetails.filter(courseDetail =>
    courseDetail.title_session.toLowerCase().includes(searchText.toLowerCase())
  );


  const handleChangeSessionField = (field, value) => {
      setSelectedSession(prevState => ({
          ...prevState,
          [field]: value
      }));
  };
  
  const handleDelete = async (courseDetailId, sessionId) => {
    const deleteUrl = `https://api.academy.eurekagroup.id/course-details/delete/${courseDetailId}/${sessionId}`;
  
    // Retrieve the Bearer token (from localStorage, sessionStorage, or context)
    const token = localStorage.getItem('token'); // Replace with actual token retrieval
  
    try {
      // Delete the session with Bearer token for authorization
      await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`, // Add Bearer token to headers
        }
      });
  
      setCourseDetails((prev) =>
        prev.map((courseDetail) =>
          courseDetail._id === courseDetailId
            ? {
                ...courseDetail,
                sessions: courseDetail.sessions.filter((session) => session._id !== sessionId),
              }
            : courseDetail
        )
      );
      setMessage('Session deleted successfully.');
      setSuccessPopup(true); // Optional: Show success notification
    } catch (error) {
      console.error('Error deleting session:', error);
      setMessage('Failed to delete session.');
      setErrorPopup(true); // Optional: Show error notification
    }
  };  

  const confirmDelete = (courseDetailId, sessionId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(courseDetailId, sessionId); // Proceed with deletion
      }
    });
  };  

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <h1 style={{ color: '#2d3436', fontFamily: 'Poppins', fontSize: '20px' }}>
                    Course Detail Management
                  </h1>
                  <Box>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      value={searchText}
                      onChange={handleSearchChange}
                      sx={{ marginRight: 2, borderRadius: '8px', backgroundColor: '#f5f5f5' }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog(null)}
                      sx={{
                        background: '#3f51b5',
                        color: 'white',
                        borderRadius: '20px',
                        '&:hover': { background: 'linear-gradient(45deg, #0984e3, #00b894)' },
                        padding: '10px 20px',
                      }}
                    >
                      Add Course Detail
                    </Button>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              <TableContainer sx={{ marginTop: '-30px', overflow: 'visible' }}>
              <Table>
  <TableHead>
    <TableRow>
      <TableCell>No</TableCell>
      <TableCell>Course</TableCell>
      <TableCell>Title</TableCell>
      <TableCell>Session Details</TableCell>
      <TableCell>Total Time Limit</TableCell>
      <TableCell>Actions</TableCell>
    </TableRow>
  </TableHead>
  
  <TableBody>
  {filteredCourseDetails
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((courseDetail, index) => {
      const sessionDetails = courseDetail.sessions.map((session) => (
        <div key={session._id} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ flex: 1 }}>
            {session.title_isi_session} : {session.session_category}
          </span>
          <span>
            <Button
              onClick={() => handleEditSession(session._id)}
              sx={{ textTransform: 'none', padding: 0 }}
            >
              Edit
            </Button>
            <Button
  onClick={() => confirmDelete(courseDetail._id, session._id)} // Pass courseDetailId and sessionId
  sx={{ textTransform: 'none', padding: 0, marginLeft: 1 }}
>
  Delete
</Button>
          </span>
        </div>
      ));

      const totalTimeLimit = courseDetail.sessions.reduce((total, session) => total + session.time_limit, 0);

      return (
        <TableRow hover key={courseDetail._id}>
          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
          <TableCell>{courseDetail.id_course ? courseDetail.id_course.title_course : 'No Course'}</TableCell>
          <TableCell>{courseDetail.title_session}</TableCell>
          <TableCell style={{ minWidth: '300px' }}>{sessionDetails}</TableCell>
          <TableCell>{totalTimeLimit} minutes</TableCell>
          <TableCell>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleOpenDialog(courseDetail)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDeleteClick(courseDetail._id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    })}
</TableBody>

</Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredCourseDetails.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
  open={openDialog}
  onClose={(event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseDialog();
  }}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    {selectedCourseDetail ? "Edit Course Detail" : "Add Course Detail"}
  </DialogTitle>
  <DialogContent>
    <Grid container spacing={2}>
      {/* Course Selection - Full Width */}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Course</InputLabel>
          <Select
            id="course-select"
            label="Course"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Course" }}
          >
            <MenuItem value="" disabled></MenuItem>
            {courses.map((courseItem) => (
              <MenuItem key={courseItem._id} value={courseItem._id}>
                <Tooltip title={courseItem.description || "No description available"}>
                  <span>{courseItem.title_course}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Form Input dalam Dua Kolom */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Title Session"
          fullWidth
          variant="outlined"
          value={titleSession}
          onChange={(e) => setTitleSession(e.target.value)}
          error={errors.titleSession}
          helperText={errors.titleSession ? "Title is required" : ""}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        {!selectedCourseDetail && (
          <FormControl fullWidth>
            <InputLabel>Session Category</InputLabel>
            <Select
              value={sessionCategory}
              onChange={(e) => setSessionCategory(e.target.value)}
              label="Session Category"
            >
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="quiz">Quiz</MenuItem>
            </Select>
          </FormControl>
        )}
      </Grid>

      {/* Video URL atau Text Content (Hanya 1 Kolom) */}
      {sessionCategory === "video" && (
        <Grid item xs={12}>
          <TextField
            label="Video URL"
            fullWidth
            variant="outlined"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
        </Grid>
      )}
      {sessionCategory === "text" && (
        <Grid item xs={12}>
          <TextField
            label="Text Content"
            fullWidth
            variant="outlined"
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
          />
        </Grid>
      )}

      {/* Form Input dalam Dua Kolom */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Number of Sessions"
          fullWidth
          variant="outlined"
          type="number"
          value={numberSession}
          onChange={(e) => setNumberSession(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        {!selectedCourseDetail && (
          <TextField
            label="Time Limit (Minutes)"
            fullWidth
            variant="outlined"
            type="number"
            value={timeLimit}
            onChange={(e) => setTimeLimit(e.target.value)}
          />
        )}
      </Grid>
    </Grid>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
    <Button onClick={handleSaveCourseDetail} color="primary">
      {selectedCourseDetail ? "Save" : "Add"} Course Detail
    </Button>
  </DialogActions>
</Dialog>


      {/* Edit Session Details Modal */}
      <Dialog
  open={openEditSessionDialog}
  onClose={() => {} /* Jangan lakukan apa-apa saat klik luar */}
  disableBackdropClick // Mencegah penutupan dialog ketika klik luar
>
            <DialogTitle>Edit Session Details</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={selectedSession?.session_category || ''}
                                onChange={(e) => handleChangeSessionField('session_category', e.target.value)}
                            >
                                <MenuItem value="video">Video</MenuItem>
                                <MenuItem value="quiz">Quiz</MenuItem>
                                {/* Add other categories if needed */}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Time Limit"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={selectedSession?.time_limit || ''}
                            onChange={(e) => handleChangeSessionField('time_limit', e.target.value)}
                        />
                    </Grid>
                    {selectedSession?.session_category === 'video' && (
                        <Grid item xs={12}>
                            <TextField
                                label="Video URL"
                                variant="outlined"
                                fullWidth
                                value={selectedSession?.video_url || ''}
                                onChange={(e) => handleChangeSessionField('video_url', e.target.value)}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
            <Button onClick={() => setOpenEditSessionDialog(false)} color="primary">
      Cancel
    </Button>
    <Button onClick={handleSaveSessionDetails} color="primary">
      Save
    </Button>
            </DialogActions>
        </Dialog>

      <Snackbar
        open={successPopup}
        autoHideDuration={6000}
        onClose={() => setSuccessPopup(false)}
        message={message}
      />
    </>
  );
};

export default CourseDetailPage;
